import React, { useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { Container } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { TypedMemo } from '../../common/types';
import { SMLContainerProps } from './props';
import { SMLSideBar } from '../../music24/SMLSideBar';
import { MobileNav } from '../../music24/navigation/mobile/MobileNav';
import { useNavBadges } from '../../music24/navigation/hooks/useNavBadges';
import { useWorkingFavicon } from '../../music24/navigation/hooks/useWorkingFavicon';
import { useSidebarRail } from '../../music24/navigation/hooks/useSidebarRail';
import { useHideLegacyMobileNavbar } from '../../music24/navigation/hooks/useHideLegacyMobileNavbar';
import { useIsNativeEmbed } from '../../music24/navigation/hooks/useIsNativeEmbed';
import { SMLPricing } from '../../music24/components/SMLPricing';
import { CommandPalette } from '../../music24/components/CommandPalette/CommandPalette';
import { WishBoxHost } from '../../music24/wishBox/WishBoxHost';
import { GlobalSMLUpgradeModal } from '../../music24/components/SMLUpgradeModal/GlobalSMLUpgradeModal';
import { WatermarkUpsellHost } from '../../music24/watermarkUpsell/WatermarkUpsellHost';
import { DemoArtistNotice } from '../../accountSelector/DemoArtistNotice';
import { ReadOnlyArtistNotice } from '../../accountSelector/ReadOnlyArtistNotice';
import { useCommandPalette } from '../../music24/hooks/useCommandPalette';
import { useOpenCheckoutIntentPricingModal } from '../../music24/hooks/useOpenCheckoutIntentPricingModal';
import { clearCheckoutIntent } from '../../music24/config/checkoutIntent';
import { useShowSMLPricingModal, useTourActive, useUserTokenDecoded } from '../../../redux/slices/appInfo/hooks';
import { toggleSMLPricingModal } from '../../../redux/slices/appInfo/slice';
import { useWelcomeRedirect } from '../../onboarding/useWelcomeRedirect';
import { PublishHost } from '../../music24/promote/publish/PublishHost';
import { AddAssetHost } from '../../music24/promote/addedAssets/AddAssetHost';
import { useOnboardingRealtime } from '../../onboarding/useOnboardingRealtime';
import { useSyncSignupIntent } from '../../onboarding/useSyncSignupIntent';
import { TourGuideBar } from '../../onboarding/tour/TourGuideBar';
import { useTourResume } from '../../onboarding/tour/useTourResume';
import { useTourToastActions } from '../../onboarding/tour/useTourToastActions';
import { LoadingView } from '../../common/views/LoadingView';
import styles from './SMLContainer.module.scss';

const SMLContainerComponent: React.FC<React.PropsWithChildren<SMLContainerProps>> = ({
  children,
  header,
  hidePricingModal = false,
  publicMode = false,
  fullHeight = false,
}) => {
  const userTokenDecoded = useUserTokenDecoded();
  useOnboardingRealtime();
  useSyncSignupIntent();
  const isAuthenticated = userTokenDecoded !== null;
  // The sidebar is for authenticated users only — its links require login.
  // Anonymous public-share viewers see only the top SmartLinksNavbar (which
  // swaps the AccountSelector for a "Start a 7-day trial" button) and the content.
  const showSidebar = !publicMode || isAuthenticated;
  const isTourActive = useTourActive();
  // Design §4: inside a native app's WebView the app owns the navigation, so the page draws none.
  const isNativeEmbed = useIsNativeEmbed();
  const showNav = showSidebar && !isNativeEmbed;
  // Nothing draws the badges inside a native app's web view, so their four requests are skipped.
  const navBadges = useNavBadges({ enabled: showNav });
  // A native web view shows no tab favicon.
  useWorkingFavicon({ enabled: showNav });
  const { isRail, toggleRail } = useSidebarRail();
  useHideLegacyMobileNavbar(showSidebar);
  const dispatch = useDispatch();
  const isPricingModalOpen = useShowSMLPricingModal();
  const { isOpen: isCommandPaletteOpen, close: closeCommandPalette } = useCommandPalette({ enabled: showNav });

  const canOnboard = isAuthenticated && !publicMode && !hidePricingModal;
  const { holdPage, welcomePending } = useWelcomeRedirect({ enabled: canOnboard });
  // Ruling F4: a tour that runs on another device goes on here. The native apps draw their own tour.
  useTourResume({ enabled: isAuthenticated && !publicMode && !isNativeEmbed });
  useTourToastActions();

  // The app shell does not paywall a Free user — the one thing that opens the pricing modal by
  // itself is a checkout intent left by a website "Choose <plan>" button (ruling C2). Only
  // `publicMode`/`hidePricingModal` force it closed.
  useEffect(() => {
    if (publicMode || hidePricingModal) {
      dispatch(toggleSMLPricingModal({ shouldShow: false }));
    }
  }, [dispatch, hidePricingModal, publicMode]);

  // The plan window opens after the welcome page and the tour, never over them. While the first
  // answer loads, the window waits. A failed or offline answer does not block it (fail open).
  const blockedByOnboarding = welcomePending || isTourActive;
  useOpenCheckoutIntentPricingModal({ enabled: canOnboard, blockedByOnboarding });

  const handleTogglePricingModal = useCallback(() => {
    // The intent has done its job once the window closes — whether the visitor checked out or
    // just dismissed it, it must not reopen it again on the next page.
    clearCheckoutIntent();
    dispatch(toggleSMLPricingModal({ shouldShow: !isPricingModalOpen }));
  }, [dispatch, isPricingModalOpen]);

  // A new user goes to the welcome page before any page of the app shows (no blink of Home).
  if (holdPage) {
    return <LoadingView />;
  }

  return (
    <>
      <div className={clsx(styles.appLayout, fullHeight && styles.appLayoutFullHeight, isRail && styles.appLayoutRail)}>
        {showNav && <SMLSideBar badges={navBadges} isRail={isRail} onToggleRail={toggleRail} />}
        <Container
          fluid
          className={clsx(
            'sml-container d-flex flex-column p-0 position-relative',
            fullHeight && styles.shellFullHeight
          )}
        >
          <div className={styles.containerContent}>
            <div
              className={clsx(
                'flex-grow-1',
                styles.mainContent,
                !showNav && styles.fullWidth,
                !showNav && styles.noBottomBar
              )}
            >
              {/* Outside <main>: the top bar is the page banner and the bottom bar its own landmark. */}
              {showNav && <MobileNav badges={navBadges} />}
              <main className={styles.main}>
                {/* The spotlight of the LaziiBee tour replaces the demo notice. It draws in a portal over the page. */}
                {isAuthenticated && !publicMode && !isNativeEmbed && isTourActive && <TourGuideBar />}
                {/* An artist above the artist limit of the plan: a slim line with the artists offer. */}
                {isAuthenticated && !publicMode && (
                  <ReadOnlyArtistNotice className={clsx('px-4 px-sm-5 pt-3', styles.inner)} />
                )}
                {/* design: music24-demo-artist a2. Renders only while the demo artist is active. */}
                {isAuthenticated && !publicMode && !isNativeEmbed && !isTourActive && (
                  <DemoArtistNotice className={clsx('px-4 px-sm-5 pt-3', styles.inner)} />
                )}
                {header !== null && (
                  <div className={clsx('px-4 px-sm-5 sml-container-header', styles.header, styles.inner)}>{header}</div>
                )}
                <div
                  className={clsx('px-4 px-sm-5', styles.inner, fullHeight && styles.bodyFullHeight, {
                    'py-4 py-sm-5': header === null,
                    'py-4': header !== null,
                  })}
                >
                  {children}
                </div>
                {/* The one-off publish dialog ("Publish to YouTube?") and its "ready" toast. */}
                {isAuthenticated && !publicMode && <PublishHost />}
                {/* The "Add …" dialog of "Add yours" (music video, lyric video, visualizer, Canvas). */}
                {isAuthenticated && !publicMode && <AddAssetHost />}
              </main>
            </div>
          </div>
        </Container>
      </div>

      {/* The plans flag opens the plans page (/app/plans). It never opens itself; it opens from
          Settings, an upgrade prompt or a checkout-intent redirect. */}
      <SMLPricing isOpen={isPricingModalOpen} toggle={handleTogglePricingModal} />

      {/* Every `M24_LIMIT_*` error, from any client, opens this. */}
      <GlobalSMLUpgradeModal />

      {/* The trial upsell on the watermark: the summary, `?upsell=watermark` and its modals. */}
      {isAuthenticated && !publicMode && <WatermarkUpsellHost />}

      {/* Command Palette (Cmd+K) */}
      <CommandPalette isOpen={isCommandPaletteOpen} onClose={closeCommandPalette} />
      <WishBoxHost />
    </>
  );
};

export const SMLContainer = TypedMemo(SMLContainerComponent);
